import { FCWithChildren } from '@types';
import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import classname from 'classnames';

import { DevicesList, RequestManagerWrapper, Information } from '@components';

import { useAppContext } from '@contexts/AppContext';
import useLayoutContext from '@hooks/useLayoutContext';

import Service from './components/Service';
import Support from './components/Support';
import StudentGreeting from './components/StudentGreeting';
import RegisteredDevices from './components/RegisteredDevices';
import AutoDetectDeviceModal from '@pages/ManageDevices/components/AutoDetectDeviceModal';
import usePageTitle from '@hooks/usePageTitle';

type HomeProps = {
  location?: {
    state: { isProfileUpdated: boolean };
  };
};

const Home: FCWithChildren<HomeProps> = ({ location }) => {
  usePageTitle();
  const history = useHistory();
  const { largeHeroImage } = useLayoutContext();
  const { user } = useAppContext();
  const { addToast } = useToasts();
  const [isSmallViewport, setIsSmallViewport] = useState(false);
  const isProfileUpdated = location && location.state && location.state.isProfileUpdated;

  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = () => setIsSmallViewport(mediaQuery.matches);

    handleViewportChange();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (isProfileUpdated) {
      addToast('Profile updated successfully', { appearance: 'success' });
    }
  }, [addToast, isProfileUpdated, history]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const studentName = user?.firstName ? user.firstName : 'student';

  const addDeviceHandler = () =>
    history.push({ pathname: '/manage-devices', state: { isAddingDevice: true } });

  const landingContainerCls = classname(
    'md:-mx-40 md:h-[24rem] bg-cover bg-center',
    largeHeroImage ? 'bg-cover bg-center text-white' : 'bg-gray-300 text-black',
  );
  const landingContainerStyle =
    isSmallViewport || !largeHeroImage ? {} : { backgroundImage: `url(${largeHeroImage})` };

  return (
    <Fragment>
      <div className={landingContainerCls} style={landingContainerStyle}>
        <StudentGreeting studentName={studentName} />
        <RequestManagerWrapper>
          <RegisteredDevices />
        </RequestManagerWrapper>
      </div>
      <div className="mt-[20px] md:mt-[172px]" aria-label="home-container">
        <RequestManagerWrapper>
          <DevicesList
            addDeviceAnchorHandler={addDeviceHandler}
            showFooter={false}
            title="My Devices"
          />
        </RequestManagerWrapper>
        <RequestManagerWrapper>
          <Service />
        </RequestManagerWrapper>
      </div>
      <RequestManagerWrapper>
        <AutoDetectDeviceModal />
      </RequestManagerWrapper>
    </Fragment>
  );
};

export default Home;
